import React, { useState, useEffect } from "react";

import { Box } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Category,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { Filters, Searches } from "../../../../../../../models/shipeezi.table";
import { Supplier } from "../../../../../../../models/supplier";
import { Warehouse } from "../../../../../../../models/warehouse";
import { ValueOf } from "../../../../../../../types";
import { Status } from "../../../../../../../util/enums";
import { paginate } from "../../../../../../../util/paginate";
import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import BadgeStatus from "../../../../../../../components/Generic/BadgeStatus";
import AddWarehouseModal from "./AddWarehouseModal";
import useStyles from "./styles";

interface SupplierWarehouseProps {
  warehouses: Warehouse[];
  overrideColumns?: Column[];
  updateSupplier: (attribute: keyof Supplier, value: ValueOf<Supplier> | null) => void;
  saveSupplier: () => void;
  viewOnly: boolean;
  goBack: () => void;
  isUpdatingAction: boolean;
  isCreate?: boolean;
}

interface SearchAttribute {
  attribute: string;
  values: string[];
}

const SupplierWarehouse = ({
  warehouses,
  updateSupplier,
  viewOnly = false,
  goBack,
  saveSupplier,
  isUpdatingAction,
  isCreate,
}: SupplierWarehouseProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState<Warehouse[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>();
  const [warehouseShow, setWarehouseShow] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const filterWarehouse = (searchAttributes: SearchAttribute[]) => {
    return searchAttributes.reduce((filteredWarehouses, searchAttribute) => {
      return filteredWarehouses.filter((warehouse: any) =>
        searchAttribute.values.some((value) =>
          searchAttribute.attribute === "status"
            ? String(warehouse[searchAttribute.attribute])
                ?.toLowerCase()
                .includes(value.toLowerCase())
            : String(warehouse[searchAttribute.attribute])
                ?.toLowerCase()
                .includes(value.toLowerCase())
        )
      );
    }, warehouses);
  };

  useEffect(() => {
    if (searchAttributes.length > 0) {
      const filteredWarehouses = filterWarehouse(searchAttributes);

      setFilteredWarehouses(paginate(filteredWarehouses, page, pageSize));
      setTotalItems(filteredWarehouses.length);
    } else {
      setFilteredWarehouses(paginate(warehouses, page, pageSize));
      setTotalItems(warehouses.length);
    }
  }, [page, pageSize, searchAttributes, warehouses]);

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (filters: Filters) => {
    const attributes: SearchAttribute[] = [];

    if (filters) {
      Object.getOwnPropertyNames(filters).forEach((attribute) => {
        attributes.push({
          attribute,
          values: filters[attribute],
        });
      });
    }
    setSearchAttributes(attributes);
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const editWarehouse = (id: string | number, row: Warehouse) => {
    const warehouse = warehouses.find(
      (warehouse) =>
        warehouse?.id === Number(id) ||
        (row?.name === warehouse?.name && row?.code === warehouse?.code)
    );

    setSelectedWarehouse(warehouse);
    setOpenModal(true);
  };

  const viewWarehouse = (id: string | number, row: Warehouse) => {
    const warehouse = warehouses.find(
      (warehouse) =>
        warehouse?.id === Number(id) ||
        (row?.name === warehouse?.name && row?.code === warehouse?.code)
    );

    setSelectedWarehouse(warehouse);
    setWarehouseShow(true);
    setOpenModal(true);
  };

  const renderStatus = (value: string) => <BadgeStatus value={value} />;
  const renderLocation = (warehouse: Warehouse) => {
    const { location } = warehouse;

    if (!location) return "";
    if (location?.longAddress) return location?.longAddress;
    const addressLine = location?.addressLine1 ? `${location?.addressLine1}` : "";
    const number = location?.number ? ` ${location?.number}` : "";
    const suburb = location?.suburb ? `, ${location?.suburb}` : "";
    const stateOrLine2 =
      location?.state || location?.addressLine2
        ? ` - ${location?.state || location?.addressLine2}`
        : "";
    const postalcode = location?.postalcode ? `, ${location?.postalcode}` : "";
    const country = location?.country ? `, ${location?.country}` : "";

    return `${addressLine}${number}${suburb}${stateOrLine2}${postalcode}${country}`;
  };

  const columns: Column[] = [
    {
      name: "code",
      title: translate("code"),
    },
    {
      name: "name",
      title: translate("name"),
    },
    {
      name: "location",
      title: translate("location"),
      formatComponent: (value, row) => renderLocation(row),
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: renderStatus,
      hideSearch: true,
      className: classes.status,
    },
  ];

  const filterCategories: Category[] = [
    {
      name: translate("status"),
      options: [
        { value: Status.ACTIVE, label: translate("active") },
        { value: Status.INACTIVE, label: translate("inactive") },
      ],
    },
  ];

  const newRecord = () => {
    if (viewOnly) return undefined;

    return {
      text: translate("create"),
      action: () => setOpenModal(true),
      tooltip: translate("addProduct"),
    };
  };

  const generalActions: GeneralActions = {
    orderBy: {
      right: ["handleNewRecord"],
      left: ["handleSearch", "handleFilterBy"],
    },
    handleNewRecord: newRecord(),
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    handleSearch: {
      onSearch: handleSearch,
    },
  };
  const changeStatus = (id: number | string, row: Warehouse) => {
    const newWarehouses = warehouses.filter((warehouse) => {
      if (warehouse?.id) {
        return warehouse?.id !== Number(id);
      }

      return row?.name !== warehouse?.name && row?.code !== warehouse?.code;
    });

    updateSupplier("warehouses", [
      ...newWarehouses,
      {
        ...row,
        status: row?.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
      },
    ]);
  };
  const addWarehouse = (warehouse: Warehouse) => {
    updateSupplier("warehouses", [...warehouses, warehouse]);
  };
  const handleUpdateWarehouse = (data: Warehouse, oldData?: Warehouse) => {
    if (oldData) {
      const newWarehouses = warehouses.filter((warehouse) => {
        if (warehouse?.id) {
          return warehouse?.id !== Number(oldData?.id);
        }

        return oldData?.name !== warehouse?.name && oldData?.code !== warehouse?.code;
      });

      updateSupplier("warehouses", [
        ...newWarehouses,
        {
          ...oldData,
          name: data?.name,
          code: data?.code,
          location: data?.location,
        },
      ]);
    } else {
      addWarehouse(data);
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setWarehouseShow(false);
    setSelectedWarehouse(undefined);
  };

  return (
    <div className={classes.mainDiv}>
      <div data-cy="warehousesGrid" className={classes.grid}>
        <ShipeeziNewGrid
          data={filteredWarehouses}
          totalItems={totalItems}
          columns={columns}
          namePage={translate("warehouses")}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [10, 20, 40, 60] : []}
          rowActions={{
            handleEdit: viewOnly ? undefined : (rowId, row) => editWarehouse(rowId, row),
            handleDetails: (rowId, row) => viewWarehouse(rowId, row),
            handleChange: viewOnly ? undefined : (rowId, row) => changeStatus(rowId, row),
          }}
          positionActionsColumn="right"
          generalActions={generalActions}
          onChangePageSize={handlePageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          emptyData={translate("noWarehousesToShow")}
          showSelection={totalItems !== 0}
          showFooterGrid={totalItems !== 0}
        />
        <AddWarehouseModal
          open={openModal}
          closeModal={closeModal}
          submit={handleUpdateWarehouse}
          view={viewOnly || warehouseShow}
          warehouse={selectedWarehouse}
        />
      </div>

      <Box
        className={classes.actionButtons}
        display="flex"
        flexDirection={"row"}
        justifyContent={"flex-end"}
      >
        <ActionButtons
          onSave={saveSupplier}
          onCancel={goBack}
          saveDisabled={viewOnly}
          cancelDisabled={isUpdatingAction}
          loading={isUpdatingAction}
          textSave={!isCreate ? undefined : translate("create")}
        />
      </Box>
    </div>
  );
};

export default SupplierWarehouse;
